import { AchievementRarity, QuestMetricType } from '../client/generated/graphql';
import { ListAchievement, ListQuest } from '../client/types';
import { i18next } from '../i18n';
import { getColor } from './color';

const rarityToColorMapping = {
    [AchievementRarity.Uncommon]: 'LIGHTGREEN_600',
    [AchievementRarity.Rare]: 'BLUE_600',
    [AchievementRarity.Epic]: 'DEEPPRUPLE_600',
    [AchievementRarity.Legendary]: 'ORANGE_600',
};

const uncommonMessages: string[] = [
    'Nice one! You scored an Uncommon achievement! Keep making life awesome 🌟',
    "Hey, you've got an Uncommon win! Keep rocking your goals 🎉",
    "Sweet progress! You're at the Uncommon level! Keep it going 💪",
    'Great job! You achieved an Uncommon feat! Keep living your best life 🎊',
    'Well done! You unlocked an Uncommon milestone! Keep striving for more 🚀',
    'Solid effort! You snagged Uncommon success! Keep crushing those goals 🔥',
    'Nice going! You earned an Uncommon achievement! Life just got better 🌈',
    "You're on a roll! Uncommon victory is yours! Keep up the momentum ⚡",
    "Way to go! Uncommon accomplishment reached! Life's getting more exciting 🌠",
    'Fantastic start! Uncommon level achieved! Keep conquering your goals 🏆',
];

const rareMessages: string[] = [
    'Amazing! You unlocked a Rare achievement! Your life game is strong 💎',
    'Wow, you achieved a Rare milestone! Keep up the awesome progress 🎊',
    "You're killing it! Rare level reached! Your life just got even better 🚀",
    'Super cool! You conquered a Rare feat! Keep making life epic ⭐',
    "You're on fire! Rare success unlocked! Keep leveling up in life 🔥",
    "Great work! Rare accomplishment achieved! You're truly crushing it 💪",
    'Kudos! You reached the Rare level! Keep turning life into an adventure 🏄',
    "Impressive! You completed a Rare milestone! Life's getting more thrilling 🌟",
];

const epicMessages: string[] = [
    "Incredible! You unlocked an Epic achievement! You're truly leveling up in life 🎆",
    'Awesome! You earned an Epic feat! Your life game is next level 🌠',
    "Mind-blowing! Epic level achieved! You're making life absolutely epic ⚡",
    'Fantastic! You secured Epic success! Your determination is inspiring 🔥',
    "Outstanding! You reached an Epic milestone! You're seriously crushing your goals 🏆",
];

const legendaryMessages: string[] = [
    "Insane! You conquered a Legendary achievement! You're a life game master 🎇",
    'Unbelievable! You unlocked the Legendary level! Your life is truly legendary 🌌',
    'Astonishing! You achieved Legendary success! Keep making life extraordinary 💫',
    'Phenomenal! You reached a Legendary milestone! Your life game is unbeatable 🔥',
    "Spectacular! You unlocked a Legendary accomplishment! You're a true goal-crusher 🏅",
];

const messagesMap = {
    [AchievementRarity.Uncommon]: uncommonMessages,
    [AchievementRarity.Rare]: rareMessages,
    [AchievementRarity.Epic]: epicMessages,
    [AchievementRarity.Legendary]: legendaryMessages,
};

export function getAchievementCompletionMessage(rarity: AchievementRarity): string {
    const messages = messagesMap[rarity];
    const randomIndex = Math.floor(Math.random() * messages.length);

    return messages[randomIndex];
}

const buttonTexts: string[] = [
    'Keep Crushing It! 💪',
    'Onward & Upward! ⬆️',
    'Keep Leveling Up! 🚀',
    "Let's go! 🚀",
    'More Wins Ahead! 🏆',
    'Keep the Momentum! ⚡',
    'Stay Awesome! 🌟',
    'Keep Conquering! 🏰',
    "Let's Do More! 🔥",
    'Never Stop Achieving! 🎯',
    'Chase More Goals! 🎯',
    'Carry on Conquering! ⚔️',
    'Reach New Heights! 🌄',
    'Pursue Greatness! 🌟',
    'Embrace the Journey! 🛤️',
    'Achieve the Extraordinary! 💫',
];

export function getRandomCompletionButtonText(): string {
    const randomIndex = Math.floor(Math.random() * buttonTexts.length);
    return buttonTexts[randomIndex];
}

export const getConditionIcon = (metricType: QuestMetricType): string => {
    switch (metricType) {
        case QuestMetricType.Experience:
            return 'auto_awesome';
        case QuestMetricType.Completion:
            return 'check_circle';
        case QuestMetricType.Time:
            return 'timer';
        case QuestMetricType.Repeating:
            return 'repeat';
        default:
            return 'help';
    }
};
export const getAchievementRarityColorCode = (rarity: AchievementRarity) => rarityToColorMapping[rarity];
export const getAchievementRarityColor = (rarity: AchievementRarity) => getColor(getAchievementRarityColorCode(rarity));

export const getAchievementMetricTypeDescription = (metricType: QuestMetricType, target: number | null): string => {
    const targetValue = target || 0;
    switch (metricType) {
        case QuestMetricType.Completion:
            return i18next.t('achievementMetrics.completeAll');
        case QuestMetricType.Experience:
            return i18next.t('achievementMetrics.earnXp', { count: targetValue });
        case QuestMetricType.Time:
            return i18next.t('achievementMetrics.spendHours', { count: targetValue });
        case QuestMetricType.Repeating:
            return i18next.t('achievementMetrics.repeatTimes', { count: targetValue });
        default:
            return i18next.t('achievementMetrics.completeAll');
    }
};

export function formatAchievementConditionQuestSummary(metricType: QuestMetricType, target: number | null, questNames: string[]): string {
    const list = questNames.filter(Boolean).join(', ') || '—';
    const n = target ?? 0;
    switch (metricType) {
        case QuestMetricType.Completion:
            return `Complete the following quests: ${list}`;
        case QuestMetricType.Repeating:
            return `Repeat ${n} times the following quests: ${list}`;
        case QuestMetricType.Experience:
            return `Earn ${n} XP from the following quests: ${list}`;
        case QuestMetricType.Time:
            return `Spend ${n} hours on the following quests: ${list}`;
        default:
            return `Complete the following quests: ${list}`;
    }
}

export interface AchievementProgress {
    percentage: number;
    completed: number;
    total: number;
    questMetrics?: Record<string, number>;
    external?: {
        level: number;
        value: number;
        target: number;
        displayedLevel?: {
            name: string;
            description: string;
            iconImageUrl?: string | null;
            iconImageColor?: string | null;
        };
        nextLevel?: {
            name: string;
            description: string;
        };
    };
}

export type AchievementLevelProgress = ListAchievement & {
    currentLevel: number;
    currentValue: number;
    currentTarget: number;
};

export const calculateAchievementProgress = (
    achievement: ListAchievement,
    cachedQuests?: ListQuest[],
    calculateDetails: boolean = false,
): AchievementProgress => {
    if (!achievement.achievementConditions || achievement.achievementConditions.length === 0)
        return { percentage: 0, completed: 0, total: 0 };

    const externalCondition = achievement.achievementConditions.find(condition => condition.metricType === QuestMetricType.External);
    if (externalCondition) {
        const levels = achievement.achievementLevels ?? [];
        const value = externalCondition.value ?? 0;
        const level = levels.filter(item => value >= item.target).length;
        const displayedLevel = levels[Math.max(level - 1, 0)];
        const nextLevel = levels[level];
        const target = nextLevel?.target ?? levels[levels.length - 1]?.target ?? externalCondition.target ?? 1;
        const percentage = Math.min(Math.round((value / target) * 100), 100);
        return {
            percentage,
            completed: Math.min(value, target),
            total: target,
            external: {
                level,
                value,
                target,
                displayedLevel,
                nextLevel,
            },
        };
    }

    if (achievement.achievedOn && !calculateDetails) return { percentage: 100, completed: 1, total: 1 };

    const condition = achievement.achievementConditions[0];
    const questIds = condition.questIds || achievement.questIds || [];
    const target = condition.metricType === QuestMetricType.Completion ? questIds.length : condition.target || 1;

    if (!cachedQuests) return { percentage: 0, completed: 0, total: 0 };

    const getQuestValue = (qid: string): number => {
        const quest = cachedQuests.find(q => q.id === qid);
        switch (condition.metricType) {
            case QuestMetricType.Completion:
                return quest?.metric?.completed ? 1 : 0;
            case QuestMetricType.Time:
                return Math.floor((quest?.metric?.timeSpentSec || 0) / 3600);
            case QuestMetricType.Experience:
                return quest?.metric?.xpPoints || 0;
            case QuestMetricType.Repeating:
                return quest?.metric?.completedTimes || 0;
            default:
                return 0;
        }
    };

    const { questMetrics, completed } = questIds.reduce(
        (acc: { questMetrics: Record<string, number>; completed: number }, qid: string) => {
            const value = getQuestValue(qid);
            acc.questMetrics[qid] = value;
            acc.completed += value;
            return acc;
        },
        { questMetrics: {} as Record<string, number>, completed: 0 },
    );

    const percentage = Math.min(Math.round((completed / target) * 100), 100);
    return { percentage, completed: Math.min(completed, target), total: target, questMetrics };
};

export const getAchievementLevelProgress = (achievement: ListAchievement): AchievementLevelProgress | null => {
    if (!achievement.achievementLevels?.length) return null;
    const external = calculateAchievementProgress(achievement).external;
    if (!external) return null;
    return {
        ...achievement,
        iconImageUrl: external.displayedLevel?.iconImageUrl ?? achievement.iconImageUrl,
        iconImageColor: external.displayedLevel?.iconImageColor ?? achievement.iconImageColor,
        currentLevel: external.level,
        currentValue: external.value,
        currentTarget: external.target,
    };
};
